import type { NodeKey } from 'lexical';

import { trackHorizontalDrag } from '../../utils/trackHorizontalDrag';
import { useMaxImageWidth, useUpdateImage } from './useUpdateImage';

const MIN_WIDTH = 40;

/** Corner handles; `dir` is +1 when dragging right grows the image, -1 when it shrinks it. */
const HANDLES = [
  { position: '-top-1.5 -left-1.5 cursor-nwse-resize', dir: -1 },
  { position: '-top-1.5 -right-1.5 cursor-nesw-resize', dir: 1 },
  { position: '-bottom-1.5 -left-1.5 cursor-nesw-resize', dir: -1 },
  { position: '-bottom-1.5 -right-1.5 cursor-nwse-resize', dir: 1 },
] as const;

type ImageResizeHandlesProps = {
  nodeKey: NodeKey;
  image: HTMLImageElement;
  /** Live width while dragging (`null` when done), so the image follows without an editor update per move. */
  onDraftWidth: (width: number | null) => void;
};

/**
 * Drag a corner to resize; only the width is stored, so the aspect ratio is always kept.
 * - The final width is committed once, on release, clamped to the editor's width.
 */
export const ImageResizeHandles = ({ nodeKey, image, onDraftWidth }: ImageResizeHandlesProps) => {
  const updateImage = useUpdateImage(nodeKey);
  const maxWidth = useMaxImageWidth();

  return HANDLES.map(({ position, dir }) => (
    <span
      key={position}
      aria-hidden
      className={`border-accent-1-100 absolute size-3 touch-none rounded-sm border-2 bg-white ${position}`}
      onPointerDown={(event) => {
        const startWidth = image.getBoundingClientRect().width;
        let latest = startWidth;

        trackHorizontalDrag(
          event,
          (deltaX) => {
            latest = Math.round(Math.min(maxWidth(), Math.max(MIN_WIDTH, startWidth + dir * deltaX)));
            onDraftWidth(latest);
          },
          () => {
            onDraftWidth(null);
            if (latest !== startWidth) updateImage((node) => node.setWidth(latest));
          },
        );
      }}
    />
  ));
};
